import { awsConfig } from '@/aws_config';
import { generateClient } from 'aws-amplify/api';
import type { GraphQLResult } from '@aws-amplify/api';
import type { V6Client } from '@aws-amplify/api-graphql';

type AppSyncAuthMode =
  | 'apiKey'
  | 'oidc'
  | 'userPool'
  | 'iam'
  | 'identityPool'
  | 'lambda'
  | 'none';

export interface IAwsConfig {
  Auth: {
    identityPoolId: string;
    region: string;
    userPoolId: string;
    userPoolWebClientId: string;
    authenticationFlowType: string;
  };
  API: {
    aws_appsync_graphqlEndpoint: string;
    aws_appsync_region: string;
    aws_appsync_authenticationType: string;
    endpoints: { name: string; endpoint: string }[];
  };
  Storage: {
    AWSS3: {
      bucket: string;
      region: string;
    };
  };
}

export interface IRequestParams {
  query: string;
  variables?: any;
  authMode?: any;
}

export interface IApiLayer {
  graphql<T>(
    endpointname: string,
    params: IRequestParams
  ): Promise<GraphQLResult<T>>;
}

export default new (class ApiLayer implements IApiLayer {
  private clients = new Map<string, V6Client>();

  private getAuthMode(): AppSyncAuthMode {
    const authType =
      awsConfig?.API?.aws_appsync_authenticationType ??
      'AMAZON_COGNITO_USER_POOLS';

    const authModeMap: Record<string, AppSyncAuthMode> = {
      AMAZON_COGNITO_USER_POOLS: 'userPool',
      API_KEY: 'apiKey',
      AWS_IAM: 'iam',
      AWS_LAMBDA: 'lambda',
      OPENID_CONNECT: 'oidc'
    };

    return authModeMap[authType] ?? 'userPool';
  }

  private getClient(endpointName = ''): V6Client {
    const key = endpointName || '__default__';

    if (!this.clients.has(key)) {
      this.clients.set(
        key,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore generateClient generic types recurse too deeply for multi-endpoint usage
        generateClient({
          endpoint: this.getEndPoint(endpointName),
          authMode: this.getAuthMode()
        } as Parameters<typeof generateClient>[0]) as V6Client
      );
    }

    const client = this.clients.get(key);
    if (!client) {
      throw new Error(`GraphQL client not found for endpoint: ${key}`);
    }

    return client;
  }

  private getEndPoint(endpointName = ''): string {
    const {
      API = {
        endpoints: [],
        aws_appsync_graphqlEndpoint: ''
      }
    } = awsConfig ?? {};
    const { endpoints = [], aws_appsync_graphqlEndpoint = '' } = API ?? {};

    if (!endpointName) {
      return aws_appsync_graphqlEndpoint;
    }

    const foundEndpointByName = endpoints.find(({ name }) => {
      return name === endpointName;
    });

    if (foundEndpointByName) {
      const { endpoint = '' } = foundEndpointByName ?? {};
      return endpoint;
    }
    return aws_appsync_graphqlEndpoint;
  }

  async graphql<T>(
    endpointname = '',
    params: IRequestParams
  ): Promise<GraphQLResult<T>> {
    const client = this.getClient(endpointname);

    return client.graphql({
      ...params,
      authMode: params.authMode ?? this.getAuthMode()
    }) as Promise<GraphQLResult<T>>;
  }
})();
